import { NodeAccessApproval, Prisma } from '@prisma/client';
import { useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';

import { BulkApprovalDto, UpdateProfileApprovalDto } from '@app/domain-t3-interfaces';
import { CommonFindResult } from '@app/infra-interfaces';
import { Axios } from '@app/web-components';

const URL = 'approval';
const APPROVAL_ALL_QUERY_KEY = 'APPROVAL_ALL_QUERY_KEY';

export const approvalKeys = {
  all: [APPROVAL_ALL_QUERY_KEY] as const,
  lists: () => [...approvalKeys.all, 'list'] as const,
  list: (filters: object) => [...approvalKeys.lists(), filters] as const,
  contractList: (contractNumber: string) => [...approvalKeys.lists(), contractNumber] as const,
  items: () => [...approvalKeys.all, 'item'] as const,
  item: (id: number | object) => [...approvalKeys.items(), id] as const,
};

export const useFindApprovals = <T>(
  args: Prisma.NodeAccessApprovalFindManyArgs,
  options?: Omit<UseQueryOptions<CommonFindResult<T>>, 'queryKey' | 'queryFn'>,
) => {
  const axios = Axios.useAxiosApi();

  return useQuery<CommonFindResult<T>>({
    ...options,
    queryKey: approvalKeys.list(args),
    queryFn: async () => {
      const { data } = await axios.post<CommonFindResult<T>>(`/${URL}/find`, args);
      return data;
    },
  });
};

export const useBulkApprove = (
  options?: Omit<
    UseMutationOptions<AxiosResponse<void>, AxiosError<{ message?: string }, unknown>, BulkApprovalDto>,
    'mutationFn'
  >,
) => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<void>, AxiosError<{ message?: string }, unknown>, BulkApprovalDto>({
    ...options,
    mutationFn: async (dto: BulkApprovalDto) => await axios.post<void>(`/${URL}/bulk`, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: approvalKeys.lists(),
      });
    },
  });
};

export const useFindApprovalsByContract = (
  contractNumber?: string,
  options?: Omit<UseQueryOptions<CommonFindResult<NodeAccessApproval>>, 'queryKey' | 'queryFn'>,
) => {
  const axios = Axios.useAxiosApi();

  return useQuery<CommonFindResult<NodeAccessApproval>>({
    ...options,
    queryKey: approvalKeys.contractList(contractNumber),
    queryFn: async () => {
      const { data } = await axios.post<CommonFindResult<NodeAccessApproval>>(`/${URL}/find`, {
        where: {
          contractNumber,
        },
      });
      return data;
    },
  });
};

export const useUpdateProfileApproval = (
  id?: number,
  options?: Omit<
    UseMutationOptions<
      AxiosResponse<NodeAccessApproval>,
      AxiosError<{ message?: string }, unknown>,
      UpdateProfileApprovalDto
    >,
    'mutationFn'
  >,
) => {
  const axios = Axios.useAxiosApi();
  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<NodeAccessApproval>,
    AxiosError<{ message?: string }, unknown>,
    UpdateProfileApprovalDto
  >({
    ...options,
    mutationFn: async (dto: UpdateProfileApprovalDto) =>
      await axios.post<NodeAccessApproval>(`/${URL}/${id}/approval`, dto),
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
  });
};
